import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSpaces } from '../context/SpaceContext';
import { getBusinesses } from '../services/businessService';
import { createProject, getProjects } from '../services/projectService';
import './WorkPage.css';

const LIFECYCLE_LABELS = {
  idea: 'Idea',
  'funding-review': 'Funding Review',
  'funding-pending': 'Funding Pending',
  approved: 'Approved',
  active: 'Active',
  'delivery-complete': 'Delivery Complete',
  reporting: 'Reporting',
  closed: 'Closed',
  'on-hold': 'On Hold',
  cancelled: 'Cancelled',
};

const formatLifecycle = (state) => LIFECYCLE_LABELS[state] || state || 'Active';

export default function WorkPage() {
  const navigate = useNavigate();
  const { spaces = [] } = useSpaces() || {};
  const [projects, setProjects] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectBusinessId, setNewProjectBusinessId] = useState('');
  const [savingProject, setSavingProject] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getProjects(), getBusinesses()])
      .then(([projectRes, businessRes]) => {
        if (!active) return;
        const nextProjects = projectRes.data.projects || [];
        const nextBusinesses = businessRes.data.businesses || [];
        setProjects(nextProjects);
        setBusinesses(nextBusinesses);
        const manageable = nextBusinesses.filter((business) =>
          (business.currentUserCapabilities || []).includes('project.manage')
        );
        if (manageable.length === 1) setNewProjectBusinessId(manageable[0]._id);
      })
      .catch((err) => console.error('Failed to load work hub:', err))
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const manageableBusinesses = useMemo(
    () => businesses.filter((business) => (business.currentUserCapabilities || []).includes('project.manage')),
    [businesses]
  );

  const recentSpaces = useMemo(
    () => [...spaces]
      .sort((left, right) => new Date(right.updatedAt || 0) - new Date(left.updatedAt || 0))
      .slice(0, 5),
    [spaces]
  );

  const activeProjects = projects.filter((project) =>
    !['closed', 'cancelled'].includes(project.lifecycleState || project.status)
  );

  const handleCreateProject = async (event) => {
    event.preventDefault();
    if (!newProjectName.trim() || !newProjectBusinessId) return;
    setSavingProject(true);
    try {
      const res = await createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim(),
        leadBusinessId: newProjectBusinessId,
        lifecycleState: 'idea',
      });
      navigate(`/projects/${res.data.project._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setSavingProject(false);
    }
  };

  return (
    <div className="work-page">
      <header className="work-header">
        <div>
          <h1>Work</h1>
          <p>Projects, Businesses and Spaces you can act on.</p>
        </div>
        {manageableBusinesses.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewProject((value) => !value)}>
            + New Project
          </button>
        )}
      </header>

      {showNewProject && manageableBusinesses.length > 0 && (
        <form className="work-create-project" onSubmit={handleCreateProject}>
          <select
            className="form-select"
            value={newProjectBusinessId}
            onChange={(event) => setNewProjectBusinessId(event.target.value)}
            required
          >
            <option value="">Choose Business *</option>
            {manageableBusinesses.map((business) => (
              <option key={business._id} value={business._id}>{business.name}</option>
            ))}
          </select>
          <input
            className="form-input"
            placeholder="Project name *"
            value={newProjectName}
            onChange={(event) => setNewProjectName(event.target.value)}
            required
          />
          <input
            className="form-input"
            placeholder="Short description"
            value={newProjectDescription}
            onChange={(event) => setNewProjectDescription(event.target.value)}
          />
          <button className="btn btn-primary btn-sm" type="submit" disabled={savingProject}>
            {savingProject ? 'Creating...' : 'Create Project'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="panel-muted">Loading work...</p>
      ) : (
        <div className="work-grid">
          <section className="work-panel work-panel-wide">
            <div className="work-panel-header">
              <h2>My Projects</h2>
              <Link to="/projects">View all</Link>
            </div>
            {activeProjects.length === 0 ? (
              <p className="panel-muted">No active projects found.</p>
            ) : (
              <div className="work-project-list">
                {activeProjects.slice(0, 8).map((project) => (
                  <Link key={project._id} className="work-project-item" to={`/projects/${project._id}`}>
                    <div>
                      <strong>{project.name}</strong>
                      <span>{project.leadBusinessId?.name || 'Business context'}</span>
                    </div>
                    <span className={`work-status status-${project.lifecycleState || project.status}`}>
                      {formatLifecycle(project.lifecycleState || project.status)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="work-panel">
            <div className="work-panel-header">
              <h2>Businesses</h2>
              <Link to="/businesses">Open</Link>
            </div>
            {businesses.length === 0 ? (
              <p className="panel-muted">No Business contexts yet.</p>
            ) : (
              <ul className="work-business-list">
                {businesses.slice(0, 6).map((business) => (
                  <li key={business._id}>
                    <Link to={`/businesses/${business._id}`}>
                      <strong>{business.name}</strong>
                      {business.industry && <span>{business.industry}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="work-panel">
            <div className="work-panel-header">
              <h2>Recent Spaces</h2>
              <Link to="/work#spaces">All</Link>
            </div>
            {recentSpaces.length === 0 ? (
              <p className="panel-muted">No Spaces yet.</p>
            ) : (
              <ul id="spaces" className="work-space-list">
                {recentSpaces.map((space) => (
                  <li key={space._id}>
                    <Link to={`/spaces/${space._id}`}>{space.name}</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="work-panel work-panel-wide">
            <div className="work-panel-header">
              <h2>More Work Tools</h2>
            </div>
            <div className="work-tool-links">
              <Link to="/wrike/supply-requests">Requests</Link>
              <Link to="/forms">Forms</Link>
              <Link to="/time">Time</Link>
              <Link to="/mileage">Mileage</Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}